export interface Result<T> {
  data: T[]
  meta?: { pagination?: { page: number; pageCount: number; total: number } }
}

export function usePaginator<T>(
  getResults: (page: number, ...args: any[]) => Promise<Result<T>>,
  key = 'paginator',
) {
  const results = useState<T[]>(`${key}-results`, () => [])
  const meta = useState<Result<T>['meta'] | null>(`${key}-meta`, () => null)
  const page = useState(`${key}-page`, () => 0)
  const savedArgs = useState<any[]>(`${key}-args`, () => [])
  const isLoading = useState(`${key}-isLoading`, () => false)
  const isStarted = useState(`${key}-isStarted`, () => false)
  const requestToken = useState<number>(`${key}-token`, () => 0)

  const setPaginator = (...args: any[]) => {
    requestToken.value++
    results.value = []
    meta.value = null
    page.value = 0
    savedArgs.value = args
    isLoading.value = false
  }

  function start(...args: any[]) {
    isStarted.value = true
    setPaginator(...args)
  }

  function reset(...args: any[]) {
    if (!isStarted.value) return
    setPaginator(...args)
  }

  async function next(loader: any = null) {
    if (isLoading.value || !isStarted.value) return
    const token = ++requestToken.value
    const nextPage = page.value + 1
    isLoading.value = true

    const pageCount = meta.value?.pagination?.pageCount
    if (meta.value && pageCount !== undefined && nextPage > pageCount) {
      loader?.complete?.()
      isLoading.value = false
      return
    }

    try {
      const { data, meta: m } = await getResults(nextPage, ...savedArgs.value)
      if (token !== requestToken.value) return // stale call

      page.value = nextPage
      meta.value = m ?? null
      results.value = nextPage === 1 ? data : [...results.value, ...data]

      const pag = m?.pagination
      const isLast = pag ? pag.page >= pag.pageCount : data.length === 0

      loader?.[isLast ? 'complete' : 'loaded']?.()
      return data
    } catch (e) {
      console.error(e)
    } finally {
      if (token === requestToken.value) isLoading.value = false
    }
  }

  return { start, next, reset, results, meta, isStarted, isLoading }
}
